import { FunctionComponent, useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { DrawerActions } from '@starjack/shared-components';
import { LoadingButton } from '@mui/lab';
import { useNotification } from '@starjack/features/notifications';
import { CreateWebshop, isOfTypeWebshop, Webshop } from '@/models/webshop';
import { MultiLangInput } from '@starjack/shared-components';
import { ColorInput } from '@/components/Layout/ColorInput';
import { LogoUploadContainer } from '@/pageContents/webshopsContent/LogoUploadContainer';
import {
    usePostWebshopMutation,
    usePutWebshopMutation,
} from '@/api/v2/configurations';
import { useGetAllLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { Select } from '@starjack/shared-components';
import { LiftOperator } from '@/models/liftoperator';
import isEqual from 'lodash/isEqual';
import { useDirtyGuard } from '@starjack/shared-components';

type WebshopDrawerContentProps = {
    webshop: Webshop | CreateWebshop;
    onClose: () => void;
};

export const WebshopDrawerContent: FunctionComponent<
    WebshopDrawerContentProps
> = ({ webshop, onClose }) => {
    const [localWebshop, setLocalWebshop] = useState<Webshop | CreateWebshop>(
        webshop
    );
    const { addNotification } = useNotification();
    const { data: liftOperators } = useGetAllLiftOperatorsQuery();
    const [putWebshop, { isLoading: putWebshopLoading }] =
        usePutWebshopMutation();
    const [postWebshop, { isLoading: postWebshopLoading }] =
        usePostWebshopMutation();
    // A new webshop stays open after creation; compare against what was saved.
    const [savedWebshop, setSavedWebshop] = useState(webshop);
    useDirtyGuard(!isEqual(localWebshop, savedWebshop));

    useEffect(() => {
        setLocalWebshop(webshop);
    }, [webshop]);

    const onChangeHandler = (key: string) => (e) => {
        const { value } = e.target;

        setLocalWebshop((prevLocalWebshop) => ({
            ...prevLocalWebshop,
            [key]: value,
        }));
    };

    const onCreateOrUpdate = () => {
        const isNewWebshop = !isOfTypeWebshop(localWebshop);
        const mutationHandler = isNewWebshop ? postWebshop : putWebshop;

        mutationHandler(localWebshop)
            .unwrap()
            .then((webshop) => {
                addNotification({
                    message: 'Speichern erfolgreich',
                    type: 'success',
                });

                setLocalWebshop(webshop);
                setSavedWebshop(webshop);

                if (!isNewWebshop) {
                    onClose();
                }
            })
            .catch((reason) => {
                console.error(reason);

                let message =
                    'Fehler beim Speichern. Bitte später noch einmal versuchen';
                if (
                    reason?.data.errors[0]?.error === 'username_already_exists'
                ) {
                    message =
                        'Ein User mit diesem Benutzernamen existiert bereits. Bitte einen anderen wählen';
                }

                addNotification({
                    message,
                    type: 'error',
                });
            });
    };

    return (
        <Box
            sx={{
                width: '520px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>
                    {isOfTypeWebshop(localWebshop)
                        ? 'Webshop bearbeiten'
                        : 'Neuen Webshop erstellen'}
                </Typography>
            </Box>

            <Stack
                spacing={6}
                sx={{
                    p: 4,
                }}
            >
                {!isOfTypeWebshop(localWebshop) ? (
                    <Stack spacing={3}>
                        <TextField
                            label='Name:'
                            value={localWebshop.vanityIdentifier}
                            onChange={onChangeHandler('vanityIdentifier')}
                            fullWidth
                        />

                        <Select
                            label='Bergbahn auswählen'
                            onChange={(e) => {
                                const { value } = e.target;

                                setLocalWebshop((prevWebshop) => ({
                                    ...prevWebshop,
                                    liftOperatorId: value === '-' ? '' : value,
                                }));
                            }}
                            value={localWebshop.liftOperatorId || '-'}
                        >
                            <option value={'-'}>--</option>
                            {liftOperators?.map(
                                (liftOperator: LiftOperator) => (
                                    <option
                                        key={liftOperator.id}
                                        value={liftOperator.id}
                                    >
                                        {liftOperator.name.de}
                                    </option>
                                )
                            )}
                        </Select>
                    </Stack>
                ) : (
                    <>
                        <Stack spacing={2}>
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                }}
                                variant='body2'
                            >
                                Hier lässt sich der individuelle Teil der
                                Shop-URL anpassen (z.B.: name.shop.starjack.at)
                            </Typography>
                            <TextField
                                label='Name:'
                                value={localWebshop.vanityIdentifier}
                                onChange={onChangeHandler('vanityIdentifier')}
                                fullWidth
                            />
                        </Stack>

                        <MultiLangInput
                            label='Impressum-URL:'
                            value={localWebshop.imprintUrl}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.imprintUrl = langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <MultiLangInput
                            label='AGB-URL:'
                            value={localWebshop.termsAndConditionsUrl}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.termsAndConditionsUrl =
                                        langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <MultiLangInput
                            label='Datenschutz-URL:'
                            value={localWebshop.dataPrivacyUrl}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.dataPrivacyUrl = langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <ColorInput
                            label='Primärfarbe:'
                            onChange={onChangeHandler('primaryColor')}
                            value={localWebshop.primaryColor}
                        />

                        <ColorInput
                            label='Primäre Farbe Hell:'
                            onChange={onChangeHandler('primaryLightColor')}
                            value={localWebshop.primaryLightColor}
                        />

                        <MultiLangInput
                            label='Ticketkategorie Tages- und Mehrtagestickets umbenennen:'
                            value={localWebshop.dayTicketsButtonText}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.dayTicketsButtonText =
                                        langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <MultiLangInput
                            label='Ticketkategorie Saison- und Jahrestickets umbenennen'
                            value={localWebshop.seasonTicketsButtonText}
                            onChange={(langString) => {
                                setLocalWebshop((prevWebshop) => {
                                    const copyWebshop = {
                                        ...prevWebshop,
                                    };
                                    copyWebshop.seasonTicketsButtonText =
                                        langString;

                                    return copyWebshop;
                                });
                            }}
                            fullWidth
                        />

                        <TextField
                            label='Tag Manager Code'
                            value={localWebshop.tagManagerCode}
                            onChange={onChangeHandler('tagManagerCode')}
                            fullWidth
                        />

                        <LogoUploadContainer
                            label='Logo'
                            subtitle={
                                <>
                                    <Typography variant='body2'>
                                        1.) Mögliche Datei-Formate: .jpg, .png
                                    </Typography>
                                    <Typography variant='body2'>
                                        2.) Die Größe des Logos passt starjack
                                        automatisch an
                                    </Typography>
                                </>
                            }
                            type='logo'
                            imageUrl={localWebshop.logoUrl}
                            webshop={localWebshop}
                        />

                        <LogoUploadContainer
                            label='Header image'
                            subtitle={
                                <>
                                    <Typography variant='body2'>
                                        1.) Mögliche Datei-Formate: .jpg, .png
                                    </Typography>
                                    <Typography variant='body2'>
                                        2.) Größe: Höhe 600 Pixel, das Bild wird
                                        automatisch mittig ausgerichtet
                                    </Typography>
                                </>
                            }
                            type='headerImage'
                            imageUrl={localWebshop.headerImageUrl}
                            webshop={localWebshop}
                        />
                    </>
                )}

                <DrawerActions>
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                        spacing={2}
                        direction='row'
                    >
                        <Button variant='outlined' onClick={onClose}>
                            Abbrechen
                        </Button>

                        <LoadingButton
                            variant='contained'
                            onClick={onCreateOrUpdate}
                            disabled={!localWebshop.liftOperatorId}
                            loading={putWebshopLoading || postWebshopLoading}
                        >
                            {isOfTypeWebshop(localWebshop)
                                ? `Speichern`
                                : `Anlegen und fortfahren`}
                        </LoadingButton>
                    </Stack>
                </DrawerActions>
            </Stack>
        </Box>
    );
};
